/**
 * plan_flow tool - Comprehensive flow planning assistant
 *
 * Provides:
 * - Natural language to flow design
 * - Best practices and naming conventions
 * - Error handling patterns
 * - Architecture recommendations
 * - Required connections and permissions
 * - Ready-to-use flow definitions
 */

import { z } from "zod";
import type { ConnectorMetadataApi } from "../api/connector-metadata.js";
import { logger } from "../utils/logger.js";

export const planFlowInputSchema = z.object({
  description: z
    .string()
    .describe("Natural language description of what the flow should do"),
  answers: z
    .record(z.string(), z.string())
    .optional()
    .describe("Answers to previous clarifying questions"),
});

export type PlanFlowInput = z.infer<typeof planFlowInputSchema>;

export const planFlowTool = {
  name: "plan_flow",
  description: `Interactive flow planning wizard. Call this FIRST when user wants to create or build a flow.

IMPORTANT: This tool returns clarifying questions that you MUST present to the user before proceeding. Do NOT skip the questions or make assumptions.

Workflow:
1. Call plan_flow with the user's description
2. Present ALL questions to the user (don't answer them yourself)
3. Collect user's answers
4. Call plan_flow again with answers to get the final plan
5. Use create_flow or build_flow with the resulting definition

Questions cover: trigger timing, recipients, data sources, formatting preferences, etc.`,
  inputSchema: {
    type: "object" as const,
    properties: {
      description: {
        type: "string",
        description: "Natural language description of what the flow should do",
      },
      answers: {
        type: "object",
        description: "Answers to previous clarifying questions (key=question id, value=answer)",
        additionalProperties: { type: "string" },
      },
    },
    required: ["description"],
  },
};

// ============================================================================
// Trigger Detection
// ============================================================================

interface TriggerPattern {
  keywords: string[];
  type: string;
  connector: string;
  operationId: string;
  description: string;
  config: Record<string, unknown>;
  bestPractices: string[];
}

const TRIGGER_PATTERNS: TriggerPattern[] = [
  {
    keywords: ["manual", "button", "click", "on demand", "when i want", "run now", "trigger manually"],
    type: "manual",
    connector: "Built-in",
    operationId: "Request/Button",
    description: "Manual trigger (button click in Power Automate mobile app or portal)",
    config: { type: "Request", kind: "Button", inputs: { schema: { type: "object", properties: {}, required: [] } } },
    bestPractices: [
      "Add input parameters if the user needs to provide data when triggering",
      "Consider adding a confirmation step for destructive actions",
    ],
  },
  {
    keywords: ["schedule", "every day", "daily", "weekly", "hourly", "monthly", "recurring", "cron", "at 8am", "at 9am", "every morning"],
    type: "scheduled",
    connector: "Built-in",
    operationId: "Recurrence",
    description: "Scheduled/recurring trigger - runs automatically at specified intervals",
    config: { type: "Recurrence", recurrence: { frequency: "Day", interval: 1, startTime: "2026-02-01T08:00:00Z", timeZone: "Central Standard Time" } },
    bestPractices: [
      "Set appropriate timezone for business hours",
      "Consider retry logic for transient failures",
      "Add monitoring/alerting for missed runs",
    ],
  },
  {
    keywords: ["email arrives", "receive email", "new email", "inbox", "when i get an email", "email received"],
    type: "email",
    connector: "Office 365 Outlook",
    operationId: "OnNewEmail",
    description: "Triggers when a new email arrives in the specified mailbox",
    config: { type: "OpenApiConnectionNotification", inputs: { host: { connectionName: "shared_office365", operationId: "OnNewEmailNotification", apiId: "/providers/Microsoft.PowerApps/apis/shared_office365" }, parameters: { folderPath: "Inbox" } } },
    bestPractices: [
      "Use folder filters to reduce trigger frequency",
      "Add conditions to filter by sender, subject, or importance",
      "Consider throttling for high-volume mailboxes",
    ],
  },
  {
    keywords: ["sharepoint", "file upload", "document", "library", "new file", "file created"],
    type: "sharepoint_file",
    connector: "SharePoint",
    operationId: "OnFileCreated",
    description: "Triggers when a file is created in a SharePoint document library",
    config: { type: "OpenApiConnectionNotification", inputs: { host: { connectionName: "shared_sharepointonline", operationId: "OnFileCreated", apiId: "/providers/Microsoft.PowerApps/apis/shared_sharepointonline" } } },
    bestPractices: [
      "Specify the exact library path to avoid unnecessary triggers",
      "Use trigger conditions to filter file types",
      "Handle duplicate triggers (SharePoint can fire multiple times)",
    ],
  },
  {
    keywords: ["sharepoint", "list item", "new item", "item created", "list"],
    type: "sharepoint_item",
    connector: "SharePoint",
    operationId: "OnItemCreated",
    description: "Triggers when an item is created in a SharePoint list",
    config: { type: "OpenApiConnectionNotification", inputs: { host: { connectionName: "shared_sharepointonline", operationId: "OnItemCreated", apiId: "/providers/Microsoft.PowerApps/apis/shared_sharepointonline" } } },
    bestPractices: [
      "Use trigger conditions with OData filters",
      "Consider batching for high-volume lists",
      "Handle concurrency with item locking patterns",
    ],
  },
  {
    keywords: ["teams", "message", "channel message", "team post", "teams notification"],
    type: "teams",
    connector: "Microsoft Teams",
    operationId: "OnNewChannelMessage",
    description: "Triggers when a new message is posted in a Teams channel",
    config: { type: "OpenApiConnectionNotification", inputs: { host: { connectionName: "shared_teams", operationId: "OnNewChannelMessage", apiId: "/providers/Microsoft.PowerApps/apis/shared_teams" } } },
    bestPractices: [
      "Filter by keywords to avoid processing all messages",
      "Use @mentions to trigger specific flows",
      "Consider rate limits on Teams API",
    ],
  },
  {
    keywords: ["form", "response", "submit", "forms", "survey", "questionnaire"],
    type: "forms",
    connector: "Microsoft Forms",
    operationId: "OnFormResponse",
    description: "Triggers when a form response is submitted",
    config: { type: "OpenApiConnectionNotification", inputs: { host: { connectionName: "shared_microsoftforms", operationId: "OnFormResponse", apiId: "/providers/Microsoft.PowerApps/apis/shared_microsoftforms" } } },
    bestPractices: [
      "Validate form data before processing",
      "Send confirmation to respondent",
      "Log responses for audit trail",
    ],
  },
  {
    keywords: ["http", "webhook", "api call", "external", "rest"],
    type: "http",
    connector: "Built-in",
    operationId: "Request/Http",
    description: "HTTP webhook trigger - external systems can call this URL",
    config: { type: "Request", kind: "Http", inputs: { schema: { type: "object" } } },
    bestPractices: [
      "Validate incoming request schema",
      "Add authentication (API key or OAuth)",
      "Return appropriate HTTP status codes",
      "Document the endpoint for consumers",
    ],
  },
];

// ============================================================================
// Action Detection
// ============================================================================

interface ActionPattern {
  keywords: string[];
  name: string;
  connector: string;
  operationId: string;
  description: string;
  premium: boolean;
  commonParams: string[];
  bestPractices: string[];
}

const ACTION_PATTERNS: ActionPattern[] = [
  {
    keywords: ["send email", "email", "mail", "notify by email", "email notification"],
    name: "Send_email",
    connector: "Office 365 Outlook",
    operationId: "SendEmailV2",
    description: "Send an email via Office 365 Outlook",
    premium: false,
    commonParams: ["To", "Subject", "Body", "Attachments", "CC", "BCC", "Importance"],
    bestPractices: [
      "Use HTML body for rich formatting",
      "Limit attachments to reasonable size (<20MB total)",
      "Include unsubscribe option for bulk emails",
      "Use dynamic content for personalization",
    ],
  },
  {
    keywords: ["teams message", "post to teams", "teams notification", "channel post", "chat message"],
    name: "Post_to_Teams",
    connector: "Microsoft Teams",
    operationId: "PostMessageToConversation",
    description: "Post a message to a Teams channel or chat",
    premium: false,
    commonParams: ["Team", "Channel", "Message", "MessageFormat"],
    bestPractices: [
      "Use Adaptive Cards for rich interactive messages",
      "Include relevant context and links",
      "Consider using @mentions to notify specific people",
    ],
  },
  {
    keywords: ["approval", "approve", "sign off", "review", "get approval"],
    name: "Start_approval",
    connector: "Approvals",
    operationId: "StartAndWaitForAnApproval",
    description: "Start an approval request and wait for response",
    premium: false,
    commonParams: ["ApprovalType", "Title", "AssignedTo", "Details", "ItemLink"],
    bestPractices: [
      "Set appropriate timeout for approvals",
      "Include all context needed for decision",
      "Handle timeout/rejection scenarios",
      "Send reminders for pending approvals",
    ],
  },
  {
    keywords: ["create sharepoint item", "add to list", "new list item"],
    name: "Create_SharePoint_item",
    connector: "SharePoint",
    operationId: "PostItem",
    description: "Create a new item in a SharePoint list",
    premium: false,
    commonParams: ["Site", "List", "Fields"],
    bestPractices: [
      "Validate data before creating",
      "Handle duplicate detection",
      "Set appropriate content type",
    ],
  },
  {
    keywords: ["update sharepoint", "modify item", "edit list item"],
    name: "Update_SharePoint_item",
    connector: "SharePoint",
    operationId: "PatchItem",
    description: "Update an existing SharePoint list item",
    premium: false,
    commonParams: ["Site", "List", "Id", "Fields"],
    bestPractices: [
      "Check item exists before updating",
      "Use version control for concurrent updates",
      "Log changes for audit trail",
    ],
  },
  {
    keywords: ["excel", "spreadsheet", "add row", "excel table", "worksheet"],
    name: "Add_Excel_row",
    connector: "Excel Online (Business)",
    operationId: "AddRowV2",
    description: "Add a row to an Excel table",
    premium: false,
    commonParams: ["Location", "DocumentLibrary", "File", "Table", "Columns"],
    bestPractices: [
      "Ensure table exists with proper headers",
      "Handle file locking errors",
      "Consider using batch operations for multiple rows",
    ],
  },
  {
    keywords: ["http request", "api call", "rest api", "web service", "call api"],
    name: "HTTP_request",
    connector: "HTTP",
    operationId: "HTTP",
    description: "Make an HTTP request to any REST API",
    premium: true,
    commonParams: ["Method", "URI", "Headers", "Body", "Authentication"],
    bestPractices: [
      "Use retry policies for transient failures",
      "Secure API keys in environment variables",
      "Validate response schema",
      "Handle rate limiting with delays",
    ],
  },
  {
    keywords: ["condition", "if", "check", "when", "branch", "decision"],
    name: "Condition",
    connector: "Built-in",
    operationId: "If",
    description: "Add a conditional branch based on a condition",
    premium: false,
    commonParams: ["Expression"],
    bestPractices: [
      "Keep conditions simple and readable",
      "Handle both true and false branches",
      "Use compose actions to simplify complex expressions",
    ],
  },
  {
    keywords: ["loop", "for each", "iterate", "apply to each", "process all"],
    name: "Apply_to_each",
    connector: "Built-in",
    operationId: "Foreach",
    description: "Loop through a collection of items",
    premium: false,
    commonParams: ["Items"],
    bestPractices: [
      "Limit items processed per run to avoid timeouts",
      "Enable concurrency for independent operations",
      "Consider batching for large collections",
    ],
  },
  {
    keywords: ["parse json", "json", "parse response"],
    name: "Parse_JSON",
    connector: "Built-in",
    operationId: "ParseJson",
    description: "Parse JSON content and make properties available",
    premium: false,
    commonParams: ["Content", "Schema"],
    bestPractices: [
      "Generate schema from sample data",
      "Handle optional/nullable properties",
      "Validate before parsing",
    ],
  },
  {
    keywords: ["compose", "set variable", "calculate", "format"],
    name: "Compose",
    connector: "Built-in",
    operationId: "Compose",
    description: "Create a data output from inputs",
    premium: false,
    commonParams: ["Inputs"],
    bestPractices: [
      "Use for intermediate calculations",
      "Name clearly to describe the value",
      "Prefer over complex nested expressions",
    ],
  },
  {
    keywords: ["delay", "wait", "pause", "sleep"],
    name: "Delay",
    connector: "Built-in",
    operationId: "Delay",
    description: "Wait for a specified amount of time",
    premium: false,
    commonParams: ["Count", "Unit"],
    bestPractices: [
      "Use sparingly - delays cost compute time",
      "Consider Delay Until for specific times",
      "Max delay is 30 days",
    ],
  },
  {
    keywords: ["attachment", "attach file", "file attachment", "include file"],
    name: "attachment",
    connector: "various",
    operationId: "attachment",
    description: "Include a file attachment in an email or message",
    premium: false,
    commonParams: ["Name", "ContentBytes"],
    bestPractices: [
      "Base64 encode binary content",
      "Check file size limits",
      "Use appropriate MIME type",
    ],
  },
  {
    keywords: ["error", "try", "catch", "handle error", "scope"],
    name: "Scope",
    connector: "Built-in",
    operationId: "Scope",
    description: "Group actions for error handling (try-catch pattern)",
    premium: false,
    commonParams: ["Actions"],
    bestPractices: [
      "Use Try scope for main logic",
      "Use Catch scope with runAfter: Failed",
      "Use Finally scope for cleanup",
      "Log errors with full context",
    ],
  },
];

// ============================================================================
// Best Practices & Patterns
// ============================================================================

const NAMING_CONVENTIONS = {
  flow: "Use verb + noun + trigger type. Example: 'Send Daily Sales Report (Scheduled)'",
  action: "Keep original action name + add context. Example: 'Send an email | Notify Approver'",
  variable: "Prefix with 'var' + camelCase. Example: 'varApprovalStatus'",
  scope: "Use Try_, Catch_, Finally_ prefixes for error handling scopes",
};

const ERROR_HANDLING_PATTERNS = [
  {
    name: "Try-Catch-Finally",
    description: "Wrap main logic in Try scope, handle errors in Catch scope, cleanup in Finally",
    when: "Use for any flow that could fail and needs graceful error handling",
  },
  {
    name: "Retry with Exponential Backoff",
    description: "Configure retry policy on HTTP actions with increasing delays",
    when: "Use for external API calls that may have transient failures",
  },
  {
    name: "Terminate on Error",
    description: "Use Terminate action to end flow with Failed status",
    when: "Use when you want the flow run history to show actual failures",
  },
  {
    name: "Error Notification",
    description: "Send email/Teams message with error details and flow run URL",
    when: "Use for critical flows that need immediate attention on failure",
  },
];

// ============================================================================
// Plan Generation
// ============================================================================

// Template definitions
const FLOW_TEMPLATES = [
  {
    id: "approval-workflow",
    name: "Approval Workflow",
    description: "Standard approval workflow with request, review, and notification stages",
    keywords: ["approv", "sign off", "review", "authorize", "request approval"],
    connectors: ["Approvals", "Office 365 Outlook"],
  },
  {
    id: "multi-channel-notification",
    name: "Multi-Channel Notification",
    description: "Send notifications via email, Teams, or push notifications",
    keywords: ["notify", "alert", "email", "teams", "message", "send"],
    connectors: ["Office 365 Outlook", "Microsoft Teams"],
  },
  {
    id: "data-sync",
    name: "Data Synchronization",
    description: "Sync data between SharePoint lists, Dataverse, or APIs",
    keywords: ["sync", "copy", "transfer", "replicate", "backup", "migrate"],
    connectors: ["SharePoint"],
  },
  {
    id: "error-handler",
    name: "Error Handler",
    description: "Robust error handling with try-catch and notifications",
    keywords: ["error", "catch", "handle", "fail", "retry", "resilient"],
    connectors: ["Office 365 Outlook"],
  },
];

interface FlowPlan {
  summary: string;
  suggestedName: string;
  /** Human label of a schedule parsed from the description, when one was. */
  schedule?: string;
  suggestedTemplate: typeof FLOW_TEMPLATES[0] | null;
  trigger: {
    type: string;
    connector: string;
    description: string;
    config: Record<string, unknown>;
    bestPractices: string[];
  } | null;
  actions: Array<{
    name: string;
    connector: string;
    description: string;
    premium: boolean;
    bestPractices: string[];
  }>;
  connections: string[];
  inputSchema: Array<{
    name: string;
    type: string;
    required: boolean;
    description: string;
  }>;
  questions: Array<{
    id: string;
    question: string;
    options?: string[];
    required: boolean;
  }>;
  errorHandling: typeof ERROR_HANDLING_PATTERNS[0] | null;
  namingConventions: typeof NAMING_CONVENTIONS;
  warnings: string[];
  flowDefinition?: {
    displayName: string;
    trigger: Record<string, unknown>;
    actions: Record<string, unknown>;
    connectionReferences: Record<string, unknown>;
  };
  ready: boolean;
}

// ============================================================================
// Schedule phrases and connector mentions
// ============================================================================

export interface ParsedSchedule {
  frequency: "Minute" | "Hour" | "Day" | "Week" | "Month";
  interval: number;
  weekDays?: string[];
  hours?: number[];
  minutes?: number[];
  label: string;
}

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
const DEFAULT_TIME_ZONE = "Central Standard Time";

/**
 * Turn "every Monday at 8am", "daily at 9:30", "every 5 minutes", "hourly" into
 * a Recurrence. A description that already says when the flow runs should not
 * be answered with "When should this flow run?" — that question was asked even
 * for "Every Monday at 8am", because the planner only looked for the word
 * "schedule". Returns null when no schedule phrase is present.
 */
export function parseSchedulePhrase(description: string): ParsedSchedule | null {
  const lower = description.toLowerCase();
  let frequency: ParsedSchedule["frequency"] | null = null;
  let interval = 1;
  let weekDays: string[] | undefined;

  const everyN = /\bevery\s+(\d+)\s+(minute|min|hour|day|week|month)s?\b/.exec(lower);
  if (everyN) {
    interval = Math.max(1, parseInt(everyN[1]!, 10));
    const unit = everyN[2]!;
    frequency = unit.startsWith("min") ? "Minute" : unit === "hour" ? "Hour" : unit === "day" ? "Day" : unit === "week" ? "Week" : "Month";
  }
  const dayMentions = WEEKDAYS.filter((d) => new RegExp(`\\b${d}s?\\b`).test(lower));
  if (dayMentions.length > 0) {
    frequency = frequency ?? "Week";
    weekDays = dayMentions.map((d) => d.charAt(0).toUpperCase() + d.slice(1));
  }
  if (/\bevery\s+weekday\b|\bweekdays\b|\bmonday\s+(?:to|through|-)\s+friday\b/.test(lower)) {
    frequency = frequency ?? "Week";
    weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  }
  if (!frequency) {
    if (/\bevery\s+minute\b/.test(lower)) frequency = "Minute";
    else if (/\bhourly\b|\bevery\s+hour\b/.test(lower)) frequency = "Hour";
    else if (/\bdaily\b|\bevery\s+(?:day|morning|evening|night)\b|\beach\s+day\b|\bonce\s+a\s+day\b/.test(lower)) frequency = "Day";
    else if (/\bweekly\b|\bevery\s+week\b|\bonce\s+a\s+week\b/.test(lower)) frequency = "Week";
    else if (/\bmonthly\b|\bevery\s+month\b|\bonce\s+a\s+month\b/.test(lower)) frequency = "Month";
  }
  if (!frequency) return null;

  let hours: number[] | undefined;
  let minutes: number[] | undefined;
  const at = /\bat\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?\b/.exec(lower);
  if (at && (frequency === "Day" || frequency === "Week" || frequency === "Month")) {
    let h = parseInt(at[1]!, 10);
    const m = at[2] ? parseInt(at[2], 10) : 0;
    const ampm = at[3]?.replace(/\./g, "");
    if (ampm === "pm" && h < 12) h += 12;
    if (ampm === "am" && h === 12) h = 0;
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      hours = [h];
      minutes = [m];
    }
  }

  const hhmm = hours !== undefined ? ` at ${String(hours[0]).padStart(2, "0")}:${String(minutes?.[0] ?? 0).padStart(2, "0")}` : "";
  const label =
    frequency === "Week" && weekDays
      ? `Weekly on ${weekDays.join(", ")}${hhmm}`
      : interval > 1
        ? `Every ${interval} ${frequency.toLowerCase()}s${hhmm}`
        : `${frequency === "Day" ? "Daily" : frequency === "Hour" ? "Hourly" : frequency === "Minute" ? "Every minute" : frequency + "ly"}${hhmm}`;
  return { frequency, interval, ...(weekDays ? { weekDays } : {}), ...(hours ? { hours, minutes } : {}), label };
}

/** Build the Recurrence trigger config the flow definition needs. */
export function recurrenceConfig(schedule: ParsedSchedule): Record<string, unknown> {
  const recurrence: Record<string, unknown> = {
    frequency: schedule.frequency,
    interval: schedule.interval,
    timeZone: DEFAULT_TIME_ZONE,
  };
  const scheduleBlock: Record<string, unknown> = {};
  if (schedule.hours) scheduleBlock.hours = schedule.hours;
  if (schedule.minutes) scheduleBlock.minutes = schedule.minutes;
  if (schedule.weekDays) scheduleBlock.weekDays = schedule.weekDays;
  if (Object.keys(scheduleBlock).length > 0) recurrence.schedule = scheduleBlock;
  return { type: "Recurrence", recurrence };
}

/** Connector mentions → connector display name and the apiId suffix a connection carries. */
const CONNECTOR_MENTIONS: Array<{ test: RegExp; connector: string; api: string }> = [
  { test: /\bjira\b/, connector: "Jira", api: "shared_jira" },
  { test: /\bmicrosoft teams\b|\bteams\b/, connector: "Microsoft Teams", api: "shared_teams" },
  { test: /\bdataverse\b|\bdynamics\s*365\b/, connector: "Microsoft Dataverse", api: "shared_commondataserviceforapps" },
  { test: /\bexcel\b/, connector: "Excel Online (Business)", api: "shared_excelonlinebusiness" },
  { test: /\bonedrive\b/, connector: "OneDrive for Business", api: "shared_onedriveforbusiness" },
  { test: /files\.com|\bfilescom\b/, connector: "Files.com", api: "shared_filescom" },
  { test: /\bmicrosoft forms\b|\bform (?:is )?(?:submitted|response)/, connector: "Microsoft Forms", api: "shared_microsoftforms" },
  { test: /\bsql\b/, connector: "SQL Server", api: "shared_sql" },
  { test: /\bplanner\b/, connector: "Planner", api: "shared_planner" },
  { test: /\bsharepoint\b|\bdocument library\b/, connector: "SharePoint", api: "shared_sharepointonline" },
  { test: /\bapprov/, connector: "Approvals", api: "shared_approvals" },
  { test: /\be-?mail\b|\boutlook\b/, connector: "Office 365 Outlook", api: "shared_office365" },
];

export const CONNECTOR_API_IDS: Record<string, string> = Object.fromEntries(CONNECTOR_MENTIONS.map((m) => [m.connector, m.api]));

export function detectConnectorMentions(description: string): string[] {
  const lower = description.toLowerCase();
  const out: string[] = [];
  for (const m of CONNECTOR_MENTIONS) if (m.test.test(lower) && !out.includes(m.connector)) out.push(m.connector);
  return out;
}

function detectTrigger(description: string): TriggerPattern | null {
  const lower = description.toLowerCase();

  let bestMatch: TriggerPattern | null = null;
  let bestScore = 0;

  for (const pattern of TRIGGER_PATTERNS) {
    const score = pattern.keywords.filter(kw => lower.includes(kw)).length;
    if (score > bestScore) {
      bestScore = score;
      bestMatch = pattern;
    }
  }

  // Default to manual if no trigger detected
  if (!bestMatch && lower.length > 0) {
    bestMatch = TRIGGER_PATTERNS[0] ?? null; // manual trigger
  }

  return bestMatch;
}

function detectActions(description: string): ActionPattern[] {
  const lower = description.toLowerCase();
  const detected: ActionPattern[] = [];
  const seen = new Set<string>();

  for (const pattern of ACTION_PATTERNS) {
    const score = pattern.keywords.filter(kw => lower.includes(kw)).length;
    if (score >= 1 && !seen.has(pattern.name)) {
      detected.push(pattern);
      seen.add(pattern.name);
    }
  }

  return detected;
}

function detectTemplate(description: string, actions: ActionPattern[]): typeof FLOW_TEMPLATES[0] | null {
  const lower = description.toLowerCase();
  let bestMatch: typeof FLOW_TEMPLATES[0] | null = null;
  let bestScore = 0;

  for (const template of FLOW_TEMPLATES) {
    let score = template.keywords.filter(kw => lower.includes(kw)).length;

    // Boost score if detected actions match template connectors
    for (const action of actions) {
      if (template.connectors.includes(action.connector)) {
        score += 0.5;
      }
    }

    if (score > bestScore && score >= 1) {
      bestScore = score;
      bestMatch = template;
    }
  }

  return bestMatch;
}

function extractEmail(description: string): string | null {
  const emailRegex = /[\w.-]+@[\w.-]+\.\w+/;
  const match = description.match(emailRegex);
  return match ? match[0] : null;
}

function generateFlowName(description: string, trigger: TriggerPattern | null): string {
  // Extract verb and noun from description
  const words = description.split(/\s+/).slice(0, 6);
  const base = words.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

  const triggerSuffix = trigger?.type === "scheduled" ? "(Scheduled)"
    : trigger?.type === "manual" ? "(Manual)"
    : trigger?.type === "email" ? "(On Email)"
    : trigger?.type.startsWith("sharepoint") ? "(SharePoint)"
    : "";

  return `${base} ${triggerSuffix}`.trim().slice(0, 80);
}

function generatePlan(description: string, answers?: Record<string, string>): FlowPlan {
  const schedule = parseSchedulePhrase(description);
  let trigger = detectTrigger(description);
  if (schedule) {
    const scheduled = TRIGGER_PATTERNS.find((t) => t.type === "scheduled");
    if (scheduled) {
      trigger = { ...scheduled, description: `Scheduled: ${schedule.label} (${DEFAULT_TIME_ZONE})`, config: recurrenceConfig(schedule) };
    }
  }
  const actions = detectActions(description);
  const suggestedTemplate = detectTemplate(description, actions);
  const email = extractEmail(description);
  const lower = description.toLowerCase();

  const questions: FlowPlan["questions"] = [];
  const warnings: string[] = [];
  const connections: string[] = [];
  const inputSchema: FlowPlan["inputSchema"] = [];

  // Every service the description names needs a connection, whether or not a
  // canned action pattern matched it (Jira, Teams, Dataverse, Excel, ...).
  for (const connector of detectConnectorMentions(description)) {
    if (!connections.includes(connector)) connections.push(connector);
  }

  // =========================================================================
  // TRIGGER QUESTIONS - Always ask about timing if not clear
  // =========================================================================
  const hasClearTrigger = schedule !== null || lower.includes("manual") || lower.includes("button") ||
    lower.includes("schedule") || lower.includes("daily") || lower.includes("hourly") ||
    lower.includes("when email") || lower.includes("when file") || lower.includes("on demand");

  if (!hasClearTrigger && !answers?.trigger_type) {
    questions.push({
      id: "trigger_type",
      question: "When should this flow run?",
      options: [
        "Manual (On demand - I'll trigger it when needed)",
        "Scheduled (Daily, hourly, etc.)",
        "When an email arrives",
        "When a file is uploaded",
        "When a form is submitted",
      ],
      required: true,
    });
  }

  // If scheduled, ask for timing details
  if (schedule === null && (answers?.trigger_type?.includes("Scheduled") || lower.includes("schedule")) && !answers?.schedule_frequency) {
    questions.push({
      id: "schedule_frequency",
      question: "How often should it run?",
      options: ["Every hour", "Daily (specify time)", "Weekly", "Monthly"],
      required: true,
    });
  }

  // =========================================================================
  // INPUT SCHEMA QUESTIONS - For manual/button triggers
  // =========================================================================
  const isManualTrigger = trigger?.type === "manual" ||
    answers?.trigger_type?.includes("Manual") ||
    lower.includes("manual") ||
    lower.includes("button") ||
    lower.includes("on demand");

  if (isManualTrigger && !answers?.needs_input) {
    questions.push({
      id: "needs_input",
      question: "Does the user need to provide any input data when triggering this flow?",
      options: [
        "No - the flow runs without input",
        "Yes - user provides text input",
        "Yes - user provides multiple fields",
      ],
      required: true,
    });
  }

  // If they said yes to input, ask what fields
  if (answers?.needs_input?.includes("Yes") && !answers?.input_fields) {
    questions.push({
      id: "input_fields",
      question: "What input fields are needed? (comma-separated, e.g., 'name, email, amount')",
      required: true,
    });
  }

  // Parse input fields into schema if provided
  if (answers?.input_fields) {
    const fields = answers.input_fields.split(",").map(f => f.trim()).filter(f => f);
    for (const field of fields) {
      inputSchema.push({
        name: field.replace(/\s+/g, "_").toLowerCase(),
        type: "string",
        required: true,
        description: field,
      });
    }
  }

  // =========================================================================
  // EMAIL ACTION QUESTIONS
  // =========================================================================
  const hasEmailAction = actions.some(a => a.operationId === "SendEmailV2") || lower.includes("email");
  if (hasEmailAction) {
    if (!connections.includes("Office 365 Outlook")) connections.push("Office 365 Outlook");

    if (!email && !answers?.recipient) {
      questions.push({
        id: "recipient",
        question: "Who should receive the email? (email address)",
        required: true,
      });
    }

    if (!answers?.subject) {
      questions.push({
        id: "subject",
        question: "What should the email subject be?",
        required: true,
      });
    }
  }

  // =========================================================================
  // DATA SOURCE QUESTIONS - Ask where data comes from
  // =========================================================================
  const needsData = lower.includes("data") || lower.includes("report") || lower.includes("csv") ||
    lower.includes("excel") || lower.includes("stock") || lower.includes("price") ||
    lower.includes("weather") || lower.includes("information");

  if (needsData && !answers?.data_source) {
    questions.push({
      id: "data_source",
      question: "Where should the data come from?",
      options: [
        "Search the internet / web API",
        "Excel file in OneDrive/SharePoint",
        "SharePoint list",
        "I'll provide the data in the flow",
        "Other (please specify)",
      ],
      required: true,
    });
  }

  // =========================================================================
  // ATTACHMENT QUESTIONS
  // =========================================================================
  const hasAttachment = lower.includes("attachment") || lower.includes("attach") ||
    lower.includes("csv") || lower.includes("file") && lower.includes("email");

  if (hasAttachment && !answers?.attachment_format) {
    questions.push({
      id: "attachment_format",
      question: "What format should the attachment be?",
      options: ["CSV file", "Excel file (.xlsx)", "PDF", "No attachment needed"],
      required: false,
    });
  }

  // =========================================================================
  // TEAMS QUESTIONS
  // =========================================================================
  const hasTeams = lower.includes("teams") || lower.includes("channel") || lower.includes("chat");
  if (hasTeams && !answers?.teams_destination) {
    if (!connections.includes("Microsoft Teams")) connections.push("Microsoft Teams");
    questions.push({
      id: "teams_destination",
      question: "Where in Teams should it post?",
      options: ["Specific channel", "Chat with me", "Group chat"],
      required: true,
    });
  }

  // =========================================================================
  // SHAREPOINT QUESTIONS
  // =========================================================================
  // "list" alone is not SharePoint — "list Jira issues" used to be planned as a
  // SharePoint flow. Only an explicit mention counts.
  const hasSharePoint = lower.includes("sharepoint") || lower.includes("document library");
  if (hasSharePoint && !answers?.sharepoint_site) {
    if (!connections.includes("SharePoint")) connections.push("SharePoint");
    questions.push({
      id: "sharepoint_site",
      question: "Which SharePoint site should it use? (URL or name)",
      required: true,
    });
  }

  // =========================================================================
  // APPROVAL QUESTIONS
  // =========================================================================
  const hasApproval = lower.includes("approv");
  if (hasApproval && !answers?.approver) {
    if (!connections.includes("Approvals")) connections.push("Approvals");
    questions.push({
      id: "approver",
      question: "Who should approve? (email address)",
      required: true,
    });
  }

  // Check for premium connectors
  const premiumActions = actions.filter(a => a.premium);
  if (premiumActions.length > 0) {
    warnings.push(`Premium connectors required: ${premiumActions.map(a => a.connector).join(", ")}. Ensure you have appropriate licensing.`);
  }

  // Add other connector requirements
  for (const action of actions) {
    if (action.connector !== "Built-in" && !connections.includes(action.connector)) {
      connections.push(action.connector);
    }
  }

  // Recommend error handling for non-trivial flows
  const errorHandling = actions.length > 1 ? (ERROR_HANDLING_PATTERNS[0] ?? null) : null;

  const plan: FlowPlan = {
    summary: description,
    // Do not stamp a trigger the wizard is still asking about into the NAME the
    // user is invited to accept — "(SharePoint)" for a Jira-triggered flow.
    suggestedName: generateFlowName(
      description,
      questions.some((q) => q.id === "trigger_type") ? null : trigger,
    ),
    ...(schedule ? { schedule: schedule.label } : {}),
    suggestedTemplate,
    trigger: trigger ? {
      type: trigger.type,
      connector: trigger.connector,
      description: trigger.description,
      config: trigger.config,
      bestPractices: trigger.bestPractices,
    } : null,
    actions: actions.map(a => ({
      name: a.name,
      connector: a.connector,
      description: a.description,
      premium: a.premium,
      bestPractices: a.bestPractices,
    })),
    connections: [...new Set(connections)],
    inputSchema,
    questions,
    errorHandling,
    namingConventions: NAMING_CONVENTIONS,
    warnings,
    ready: questions.length === 0 && trigger !== null,
  };

  // Generate flow definition if ready
  if (plan.ready && trigger) {
    const finalRecipient = email || answers?.recipient || "";
    const finalSubject = answers?.subject || "Automated Notification";

    plan.flowDefinition = buildFlowDefinition(plan, finalRecipient, finalSubject);
  }

  return plan;
}

function buildFlowDefinition(
  plan: FlowPlan,
  recipient: string,
  subject: string
): FlowPlan["flowDefinition"] {
  const actionsDef: Record<string, unknown> = {};
  const connRefs: Record<string, unknown> = {};
  let prevAction: string | null = null;

  for (const action of plan.actions) {
    if (action.name === "Send_email") {
      const actionName = "Send_email";
      actionsDef[actionName] = {
        type: "OpenApiConnection",
        inputs: {
          host: {
            connectionName: "shared_office365",
            operationId: "SendEmailV2",
            apiId: "/providers/Microsoft.PowerApps/apis/shared_office365",
          },
          parameters: {
            "emailMessage/To": recipient,
            "emailMessage/Subject": subject,
            "emailMessage/Body": `<p>This is an automated message from Power Automate.</p><p>Flow: ${plan.summary}</p>`,
          },
        },
        runAfter: prevAction ? { [prevAction]: ["Succeeded"] } : {},
      };
      connRefs["shared_office365"] = {
        connectionName: "shared_office365",
        id: "/providers/Microsoft.PowerApps/apis/shared_office365",
        source: "Embedded",
        tier: "Standard",
      };
      prevAction = actionName;
    }
  }

  if (Object.keys(actionsDef).length === 0) {
    actionsDef["Placeholder"] = {
      type: "Compose",
      inputs: "Flow executed successfully",
      runAfter: {},
    };
  }

  return {
    displayName: plan.suggestedName,
    trigger: plan.trigger?.config ?? { type: "Request", kind: "Button", inputs: { schema: { type: "object", properties: {}, required: [] } } },
    actions: actionsDef,
    connectionReferences: connRefs,
  };
}

// ============================================================================
// Handler
// ============================================================================

/**
 * Say which of the needed connectors already have a connection in the current
 * environment, so the plan names the connection to use (or the one to create)
 * instead of leaving "Connections needed" as homework. Best-effort: no API, a
 * slow call or an error just yields no lines.
 */
async function describeExistingConnections(api: ConnectorMetadataApi | null, needed: string[]): Promise<string> {
  if (!api || needed.length === 0 || typeof api.listConnections !== "function") return "";
  try {
    const connections = await Promise.race([
      api.listConnections(),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000)),
    ]);
    if (!connections) return "";
    const lines: string[] = [];
    for (const connector of needed) {
      const apiName = CONNECTOR_API_IDS[connector];
      if (!apiName) continue;
      const matches = connections.filter((c) => {
        const apiId = (c.properties as { apiId?: string }).apiId ?? "";
        return apiId.endsWith(`/${apiName}`) || apiId === apiName;
      });
      lines.push(
        matches.length > 0
          ? `  ${connector} → ${matches.map((c) => `${c.properties.displayName} (${c.name})`).join(", ")}`
          : `  ${connector} → none yet in this environment; create one before the flow can run`,
      );
    }
    return lines.length > 0 ? `**Existing connections:**\n${lines.join("\n")}\n` : "";
  } catch (error) {
    logger.debug({ error: error instanceof Error ? error.message : String(error) }, "plan_flow: connection lookup skipped");
    return "";
  }
}

export interface PlanFlowContext {
  api: ConnectorMetadataApi | null;
}

export async function handlePlanFlow(
  context: PlanFlowContext,
  input: unknown
): Promise<string> {
  const parsed = planFlowInputSchema.parse(input);

  logger.info({ description: parsed.description, hasAnswers: !!parsed.answers }, "Planning flow");

  const plan = generatePlan(parsed.description, parsed.answers);
  const connectionLines = await describeExistingConnections(context.api, plan.connections);

  // If there are questions, format them for the wizard experience
  if (plan.questions.length > 0) {
    let output = `## Flow Plan: ${plan.suggestedName}\n\n`;
    output += `**Goal:** ${plan.summary}\n\n`;

    // The wizard asks `trigger_type` precisely when it does NOT know what should
    // start the flow, so asserting a detected trigger beside that question
    // contradicts it. Verified live: "when a HELP ticket is created in Jira,
    // post to Teams and log it to a SharePoint list" reported "Trigger: Triggers
    // when an item is created in a SharePoint list" while simultaneously asking
    // when the flow should run — and offering options that did not include Jira.
    // detectTrigger had scored "sharepoint" and "list" out of the ACTION clause,
    // and Jira has no trigger pattern at all, so the match was incidental.
    const triggerUnresolved = plan.questions.some((q) => q.id === "trigger_type");
    if (triggerUnresolved) {
      output += `**Trigger:** not determined from the description — answer the trigger question below\n`;
    } else if (plan.trigger) {
      output += `**Trigger:** ${plan.trigger.description}\n`;
    }
    if (plan.actions.length > 0) {
      output += `**Actions:** ${plan.actions.map(a => a.description).join(" → ")}\n`;
    }
    if (plan.connections.length > 0) {
      output += `**Connections needed:** ${plan.connections.join(", ")}\n`;
    }
    output += connectionLines;

    // Show template suggestion if detected
    if (plan.suggestedTemplate) {
      output += `\n📋 **Suggested Template:** ${plan.suggestedTemplate.name}\n`;
      output += `   ${plan.suggestedTemplate.description}\n`;
    }

    output += `\n---\n\n`;
    output += `**Before I can create this flow, I need a few details:**\n\n`;

    plan.questions.forEach((q, i) => {
      output += `${i + 1}. **${q.question}**\n`;
      if (q.options && q.options.length > 0) {
        q.options.forEach(opt => {
          output += `   - ${opt}\n`;
        });
      }
      output += `\n`;
    });

    output += `---\n`;
    output += `_Please answer these questions, then I'll create the flow._\n`;
    output += `\n[QUESTIONS_PENDING: ${plan.questions.map(q => q.id).join(",")}]`;

    return output;
  }

  // No questions - flow is ready
  let output = `## Flow Plan: ${plan.suggestedName}\n\n`;
  output += `✅ **Ready to create!**\n\n`;

  output += `**Trigger:** ${plan.trigger?.description || "Manual"}\n`;
  output += `**Actions:** ${plan.actions.map(a => a.description).join(" → ")}\n`;

  if (plan.connections.length > 0) {
    output += `**Connections needed:** ${plan.connections.join(", ")}\n`;
  }
  output += connectionLines;

  // Show template suggestion if detected
  if (plan.suggestedTemplate) {
    output += `\n📋 **Suggested Template:** ${plan.suggestedTemplate.name}\n`;
    output += `   ${plan.suggestedTemplate.description}\n`;
    output += `   _Use this as a starting point for faster development._\n`;
  }

  // Show input schema if defined
  if (plan.inputSchema.length > 0) {
    output += `\n**Input Schema:**\n`;
    output += `| Field | Type | Required |\n`;
    output += `|-------|------|----------|\n`;
    for (const field of plan.inputSchema) {
      output += `| ${field.name} | ${field.type} | ${field.required ? "Yes" : "No"} |\n`;
    }
  }

  if (plan.warnings.length > 0) {
    output += `\n⚠️ **Warning:** ${plan.warnings[0]}\n`;
  }

  if (plan.flowDefinition) {
    output += `\n**Flow Definition:**\n`;
    output += "```json\n";
    output += JSON.stringify(plan.flowDefinition, null, 2);
    output += "\n```\n";
    output += `\n_Use create_flow with this definition, or build_flow for a simpler approach._`;
  }

  return output;
}
